import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { ThresholdDetailsParams } from '../types/compliance';

interface ThresholdDetailsPanelContextType {
  isOpen: boolean;
  params: ThresholdDetailsParams | null;
  openPanel: (params: ThresholdDetailsParams) => void;
  closePanel: () => void;
}

const ThresholdDetailsPanelContext = createContext<ThresholdDetailsPanelContextType | undefined>(undefined);

interface ThresholdDetailsPanelProviderProps {
  children: ReactNode;
}

export const ThresholdDetailsPanelProvider: React.FC<ThresholdDetailsPanelProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [params, setParams] = useState<ThresholdDetailsParams | null>(null);

  const openPanel = useCallback((newParams: ThresholdDetailsParams) => {
    setParams(newParams);
    setIsOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    // Keep params around briefly for exit animation
    setTimeout(() => setParams(null), 300);
  }, []);

  const value: ThresholdDetailsPanelContextType = {
    isOpen,
    params,
    openPanel,
    closePanel,
  };

  return (
    <ThresholdDetailsPanelContext.Provider value={value}>
      {children}
    </ThresholdDetailsPanelContext.Provider>
  );
};

export const useThresholdDetailsPanel = (): ThresholdDetailsPanelContextType => {
  const context = useContext(ThresholdDetailsPanelContext);
  if (!context) {
    throw new Error('useThresholdDetailsPanel must be used within a ThresholdDetailsPanelProvider');
  }
  return context;
};
